import type { FontFamily, FontStyle } from 'packages/types/fonts';

export type PendingFontFile = { key: string; fileName: string; fontFamilyName: string; name: string; exists: boolean };

export type UploadFailure = { fileName: string; reason: string };

export type FontUpload =
    | { status: 'checking'; total: number }
    | { status: 'confirming replace'; files: PendingFontFile[]; checkFailures: UploadFailure[] }
    | { status: 'uploading'; total: number; completed: number; failedFiles: UploadFailure[]; replaced: number };

export type DeleteScope = 'family' | 'style';

export type ToggleActivationScope = 'family' | 'style';

export type FontListStatus = 'all' | 'active' | 'deactivated';

export type FontListSortBy = 'name' | 'date_created';

export type FontListSortOrder = 'asc' | 'desc';

export type FontListQuery = {
    search: string;
    status: FontListStatus;
    sortBy: FontListSortBy;
    sortOrder: FontListSortOrder;
};

export type LoadedFonts = {
    families: FontFamily[];
    query: FontListQuery;
    selectedFontIds: number[];
    /** When this response came back, which is what makes the list stale later. */
    loadedAt: number;
};

/**
 * `loaded` is what the last successful request returned. A new request and a failed request both
 * keep it, so the page shows the list it already had instead of blanking or dropping to an error.
 */
export type FontList =
    // Declared here as undefined so `loaded` can be read without narrowing the status first.
    | { status: 'not started'; loaded?: undefined }
    | { status: 'loading'; loaded?: LoadedFonts }
    | { status: 'error'; errors: unknown; loaded?: LoadedFonts }
    | { status: 'ready'; loaded: LoadedFonts };

export type DefaultFontChange = { status: 'confirming' | 'saving'; fontId: number };

export type FontDeletion = { status: 'confirming' | 'deleting'; scope: DeleteScope; fontIds: number[] };

export type FontActivationToggle = {
    status: 'confirming' | 'toggle_activation';
    scope: ToggleActivationScope;
    mode: 'activate' | 'deactivate';
    fontIds: number[];
};

export type ReplacementCandidates =
    | { status: 'loading' }
    | { status: 'ready'; families: FontFamily[] }
    | { status: 'error' };

export type ReplacementOverwrite = { fontFamilyName: string; name: string };

export type FontReplacement = {
    status: 'choosing' | 'confirming' | 'replacing';
    font: FontStyle;
    candidates: ReplacementCandidates;
    overwrite?: ReplacementOverwrite;
};

export type FontManagementState = {
    list: FontList;
    query: FontListQuery;
    upload: FontUpload | undefined;
    deletion: FontDeletion | undefined;
    toggleActivation: FontActivationToggle | undefined;
    defaultChange: DefaultFontChange | undefined;
    replacement: FontReplacement | undefined;
    isEditorFontsStale: boolean;
};

export enum actionTypes {
    GET_FONT_FAMILIES_REQUEST = 'GET_FONT_FAMILIES_REQUEST',
    GET_FONT_FAMILIES_SUCCESS = 'GET_FONT_FAMILIES_SUCCESS',
    GET_FONT_FAMILIES_FAILURE = 'GET_FONT_FAMILIES_FAILURE',
    FONT_LIST_QUERY_CHANGED = 'FONT_LIST_QUERY_CHANGED',
    DEFAULT_FONT_CHANGE_REQUESTED = 'DEFAULT_FONT_CHANGE_REQUESTED',
    DEFAULT_FONT_CHANGE_CANCELLED = 'DEFAULT_FONT_CHANGE_CANCELLED',
    SET_DEFAULT_FONT_REQUEST = 'SET_DEFAULT_FONT_REQUEST',
    SET_DEFAULT_FONT_SUCCESS = 'SET_DEFAULT_FONT_SUCCESS',
    SET_DEFAULT_FONT_FAILURE = 'SET_DEFAULT_FONT_FAILURE',
    EDITOR_FONTS_SYNCED = 'EDITOR_FONTS_SYNCED',
    UPLOAD_FONTS_STARTED = 'UPLOAD_FONTS_STARTED',
    UPLOAD_REPLACE_REQUESTED = 'UPLOAD_REPLACE_REQUESTED',
    UPLOAD_FONTS_UPLOADING = 'UPLOAD_FONTS_UPLOADING',
    UPLOAD_FONT_SUCCESS = 'UPLOAD_FONT_SUCCESS',
    UPLOAD_FONT_FAILURE = 'UPLOAD_FONT_FAILURE',
    UPLOAD_FONTS_FINISHED = 'UPLOAD_FONTS_FINISHED',
    FONT_SELECTION_TOGGLED = 'FONT_SELECTION_TOGGLED',
    FONT_SELECTION_CLEARED = 'FONT_SELECTION_CLEARED',
    DELETE_FONTS_REQUESTED = 'DELETE_FONTS_REQUESTED',
    DELETE_FONTS_CANCELLED = 'DELETE_FONTS_CANCELLED',
    DELETE_FONTS_REQUEST = 'DELETE_FONTS_REQUEST',
    DELETE_FONTS_SUCCESS = 'DELETE_FONTS_SUCCESS',
    DELETE_FONTS_FAILURE = 'DELETE_FONTS_FAILURE',
    TOGGLE_ACTIVATE_FONTS_REQUESTED = 'TOGGLE_ACTIVATE_FONTS_REQUESTED',
    TOGGLE_ACTIVATE_FONTS_CANCELLED = 'TOGGLE_ACTIVATE_FONTS_CANCELLED',
    TOGGLE_ACTIVATE_FONTS_REQUEST = 'TOGGLE_ACTIVATE_FONTS_REQUEST',
    TOGGLE_ACTIVATE_FONTS_SUCCESS = 'TOGGLE_ACTIVATE_FONTS_SUCCESS',
    TOGGLE_ACTIVATE_FONTS_FAILURE = 'TOGGLE_ACTIVATE_FONTS_FAILURE',
    REPLACE_FONT_REQUESTED = 'REPLACE_FONT_REQUESTED',
    REPLACE_FONT_CANCELLED = 'REPLACE_FONT_CANCELLED',
    REPLACE_FONT_CANDIDATES_SUCCESS = 'REPLACE_FONT_CANDIDATES_SUCCESS',
    REPLACE_FONT_CANDIDATES_FAILURE = 'REPLACE_FONT_CANDIDATES_FAILURE',
    REPLACE_FONT_REQUEST = 'REPLACE_FONT_REQUEST',
    REPLACE_FONT_OVERWRITE_REQUESTED = 'REPLACE_FONT_OVERWRITE_REQUESTED',
    REPLACE_FONT_OVERWRITE_DECLINED = 'REPLACE_FONT_OVERWRITE_DECLINED',
    REPLACE_FONT_UPLOADED = 'REPLACE_FONT_UPLOADED',
    REPLACE_FONT_SUCCESS = 'REPLACE_FONT_SUCCESS',
    REPLACE_FONT_FAILURE = 'REPLACE_FONT_FAILURE',
}
